// GA4 funnel analytics (#16). Safe-by-construction: if GA4 has not been
// enabled for the project yet (no measurementId in config), getAnalytics
// throws in some environments and is a no-op in others — so every call goes
// through track(), which swallows any analytics error. The product funnel
// must never break because telemetry is missing.
//
// PRIVACY: vocabkit is children-adjacent. Events carry NO child names, no
// word lists, no photo data — only funnel stage + coarse counts + theme slug.
// GA4 property must be marked "child-directed" and non-personalized ads
// before pilot (issue #18/#2 gate).
import { getAnalytics, logEvent, type Analytics } from "firebase/analytics";
import type { FirebaseApp } from "firebase/app";

let analytics: Analytics | null = null;
let tried = false;

function ensure(app: FirebaseApp): Analytics | null {
  if (tried) return analytics;
  tried = true;
  // GA4 must be enabled for the project (measurementId present) before
  // getAnalytics works; without it, stay silent — analytics.ts no-ops.
  if (import.meta.env.VITE_FIREBASE_MEASUREMENT_ID !== "disabled") {
    try {
      analytics = getAnalytics(app);
    } catch {
      analytics = null; // GA4 not enabled for the project yet
    }
  }
  return analytics;
}

export function initAnalytics(app: FirebaseApp): void {
  ensure(app);
}

export function track(event: string, params?: Record<string, string | number | boolean>): void {
  const a = analytics;
  if (!a) return;
  try {
    logEvent(a, event, params);
  } catch {
    /* telemetry must never throw into the product */
  }
}

// Funnel events (dashboards read these names — keep stable):
//   sign_up                  — first auth state (uid present, new session)
//   intake_start             — wizard opened
//   intake_extract           — photos uploaded + vision extraction succeeded (photo_count)
//   intake_review_complete   — parent clicked through review step
//   kit_generate             — kit queued (word_count, theme, art_enabled)
//   kit_ready                — pipeline finished (word_count, theme)
//   artifact_download        — parent opened/printed a deliverable (kind)
//   paywall_seen             — plan gate closed mid-flow (from)
//   landing_cta              — public landing page CTA clicked (where)
export const EVENTS = {
  signUp: "sign_up",
  intakeStart: "intake_start",
  intakeExtract: "intake_extract",
  reviewComplete: "intake_review_complete",
  kitGenerate: "kit_generate",
  kitReady: "kit_ready",
  artifactDownload: "artifact_download",
  paywallSeen: "paywall_seen",
  paywallUpgrade: "paywall_upgrade",
  checkoutStart: "checkout_start",
  landingCta: "landing_cta",
} as const;
